/* Tour field-state store
   -----------------------
   The guided tour's field-completion gates (Step 3 client-select,
   Step 4 task-details, Step 6 from-date, Step 7 to-date) need to
   know the current sidebar formData. We can't pass it through props
   without coupling the sidebar to the tour, and the existing event
   bus is single-shot ("the event happened") — for gates we need a
   CONTINUOUS signal that re-evaluates as the user types.

   This module holds the latest value of each tracked field in a
   module-scoped object and notifies subscribers on every change.
   The sidebar calls `setFieldState('clientId', value)` on form
   updates; the tour hook calls `getFieldState('clientId')` and
   subscribes to re-render. No global side-effects: the keys are
   tour-specific (`tour:*`) and unused outside this feature. */

export type TourFieldKey =
  | 'tour:clientId'
  | 'tour:taskDetails'
  | 'tour:fromDate'
  | 'tour:toDate'
  | 'tour:pricingType'
  | 'tour:price';

type Snapshot = Partial<Record<TourFieldKey, unknown>>;
let snapshot: Snapshot = {};
const listeners = new Set<() => void>();

export const setFieldState = (key: TourFieldKey, value: unknown): void => {
  if (snapshot[key] === value) return; // skip no-op pushes
  snapshot = { ...snapshot, [key]: value };
  // Snapshot before iterating so a listener unsubscribing during the
  // notification doesn't trip the Set iterator.
  Array.from(listeners).forEach((fn) => {
    try { fn(); } catch (_) { /* swallow */ }
  });
};

export const getFieldState = (key: TourFieldKey): unknown => snapshot[key];

export const subscribeToFieldState = (fn: () => void): (() => void) => {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
};

/* Clear when the tour closes so a future tour session starts fresh —
   avoids "Next was enabled because the previous tour-attempt's data
   is still cached" surprises. */
export const resetFieldState = (): void => {
  snapshot = {};
  Array.from(listeners).forEach((fn) => {
    try { fn(); } catch (_) { /* swallow */ }
  });
};
